import { Component, Injector, OnInit } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { finalize } from 'rxjs';

import { ConfirmationModalComponent } from '@app/components/modals/confirmation-modal/confirmation-modal.component';
import { DocumentsService } from '@app/ptrab/services/documents/documents.service';
import { DocumentStatus } from '@app/ptrab/shared/enums/document-status.enum';
import { Document } from '@app/ptrab/shared/models/document';
import { AlertService, ModalManager, NavigationEvent, NavigationEvents, NavigationService } from '@app/services';
import { ActionsAnalytics, CategoriesAnalytics } from '@app/services/analytics/models/analytics.enum';
import { LoadingService } from '@app/services/loading/loading.service';
import { UserService } from '@app/services/user/user.service';
import { BasePageDirective } from '@app/shared/bases/base.page';
import { PAGES } from '@app/shared/enums/pages/pages.enum';
import { OnBoardingRole } from '@app/shared/interfaces/user/user-onboarding.interface';
import { Buttons } from '@app/shared/models/buttons/buttons';
import { OnBoarding } from '@app/shared/models/user/user-onboarding.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-on-boarding',
  templateUrl: './on-boarding.page.html',
  styleUrls: ['./on-boarding.page.scss']
})
export class OnBoardingPage extends BasePageDirective implements OnInit {
  isWelcome = true;
  currentStep = 1;
  backDisabled = false;
  nextDisabled = true;
  backButtonText = 'STEPPER.BACK';
  nextButtonText = 'STEPPER.NEXT';
  boardingData!: OnBoarding;
  document!: Document;
  userIsNewWorker!: boolean;

  constructor(
    private navigationService: NavigationService,
    private userService: UserService,
    private loadingService: LoadingService,
    private documentsService: DocumentsService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private modalManager: ModalManager,
    injector: Injector
  ) {
    super(injector);
  }

  get stepperTitle() {
    let title = '';

    switch (this.currentStep) {
      case 1:
        title = 'ON_BOARDING.STEPPER.FIRST_STEP.TITLE';
        break;
      case 2:
        title = 'ON_BOARDING.STEPPER.SECOND_STEP.TITLE';
        break;
      case 3:
        title = 'ON_BOARDING.STEPPER.THIRD_STEP.TITLE';
        break;
    }

    return title;
  }

  ngOnInit() {
    this.loadOnBoardingData();
  }

  onBeginClicked() {
    this.loadingService.show();
    this.sendAnalyticsEvent(ActionsAnalytics.START_ON_BOARDING);
    this.documentsService
      .getOnBoardingDocument()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (document) => {
          if (document) {
            this.document = document;

            if (document.status === DocumentStatus.IN_PROGRESS) {
              this.alertService.showWarning(
                this.translateService.instant('PROCESSING_DOCUMENT_TITLE'),
                this.translateService.instant('PROCESSING_DOCUMENT_DESCRIPTION')
              );

              return;
            }

            this.isWelcome = false;
            return;
          }

          this.updateStep(true, false);
          this.isWelcome = false;
        },
        error: (e) => {
          if (e.error && e.error.message) {
            return;
          }

          this.alertService.showError(
            this.translateService.instant('ERROR_MESSAGES.ERROR'),
            this.translateService.instant('ERROR_MESSAGES.GENERIC_FAIL_MESSAGE')
          );
        }
      });
  }

  onSignedContract() {
    this.nextDisabled = false;
  }

  async updateStep(goingForward: boolean, sendAnalytics = true) {
    if (goingForward) {
      if (this.currentStep === 4) {
        this.userService.updateUserConditions().subscribe(() => {
          this.navigationService.navigate(new NavigationEvent(NavigationEvents.SetRoot, { path: PAGES.HOME }));
        });
      }
      this.currentStep++;
    } else {
      this.currentStep--;
    }

    switch (this.currentStep) {
      case 1:
        this.isWelcome = true;
        this.nextDisabled = true;
        break;
      case 2:
        this.nextDisabled = false;
        this.backDisabled = false;
        if (goingForward && sendAnalytics) {
          this.sendAnalyticsEvent(`${ActionsAnalytics.NEXT_ON_BOARDING}_signed_document`);
        }
        break;
      case 3:
        this.nextButtonText = 'STEPPER.NEXT';
        if (goingForward) {
          this.sendAnalyticsEvent(`${ActionsAnalytics.NEXT_ON_BOARDING}_informative_videos`);
        }
        break;
      case 4:
        if (!this.boardingData.completed) {
          this.storeOnBoarding();
        }
        this.nextButtonText = 'STEPPER.FINALIZE';
        if (goingForward) {
          this.sendAnalyticsEvent(`${ActionsAnalytics.NEXT_ON_BOARDING}_training_videos`);
        }
        break;
      default:
        this.isWelcome = true;
        this.nextDisabled = true;
        this.currentStep = 1;
    }
  }

  logout() {
    const title = 'ON_BOARDING.LOGOUT_TITLE';
    const contents = ['ON_BOARDING.LOGOUT_DESCRIPTION'];
    const buttons: Buttons[] = [
      {
        text: 'NO',
        type: 'secondary',
        enabled: true,
        onClick: () => {
          this.modalManager.dismissMatModal();
        }
      },
      {
        text: 'YES',
        type: 'primary',
        enabled: true,
        onClick: () => {
          this.modalManager.dismissMatModal();
          const navEvent = new NavigationEvent(NavigationEvents.SetRoot, { path: PAGES.LOGOUT });
          this.navigationService.navigate(navEvent);
        }
      }
    ];
    const modalOpts: MatDialogConfig = {
      data: { title, contents, buttons },
      disableClose: true,
      panelClass: ['media-content-modal', 'confirmation-two-lines-modal']
    };

    this.modalManager.openMatModal(ConfirmationModalComponent, modalOpts);
  }

  private loadOnBoardingData() {
    this.loadingService.show();
    this.userService
      .getOnBoarding()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe((onBoardingData) => {
        this.boardingData = onBoardingData;
        this.userIsNewWorker = this.boardingData.role === OnBoardingRole.NEW;
      });
  }

  private storeOnBoarding() {
    this.loadingService.show();
    this.userService
      .updateOnBoarding()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => (this.boardingData.completed = true));
  }

  private sendAnalyticsEvent(eventLabel: string) {
    this.analyticsService.sendEvent(CategoriesAnalytics.CLICK, {
      [ActionsAnalytics.CLICKACTION]: eventLabel
    });
  }
}
